import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from 'app/core/pipe/translate.pipe';
import { NetworkStatusService } from 'app/core/services/network-status.service';
import { ToasterService } from 'app/core/services/toaster.service';
import { ErrorPageLayoutComponent } from 'app/shared/components/atoms/error-page-layout';
import { Subject, skip, takeUntil } from 'rxjs';

/**
 * Full-screen overlay that appears when the device loses internet connectivity.
 * Auto-dismisses with a toaster when connectivity is restored.
 *
 * Place once at the top level of your layout:
 *   <app-offline-overlay></app-offline-overlay>
 */
@Component({
    selector: 'app-offline-overlay',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        TranslatePipe,
        ErrorPageLayoutComponent,
    ],
    templateUrl: './offline-overlay.component.html',
    styles: [
        `
            :host {
                display: contents;
            }
        `,
    ],
})
export class OfflineOverlayComponent implements OnInit, OnDestroy {
    private readonly _networkStatus = inject(NetworkStatusService);
    private readonly _toaster = inject(ToasterService);
    private readonly _destroy$ = new Subject<void>();

    isOffline = false;

    ngOnInit(): void {
        this._networkStatus.isOnline$
            .pipe(takeUntil(this._destroy$))
            .subscribe((online) => {
                this.isOffline = !online;
            });

        // Show "back online" toaster when connectivity is restored (skip initial emit)
        this._networkStatus.isOnline$
            .pipe(skip(1), takeUntil(this._destroy$))
            .subscribe((online) => {
                if (online) {
                    this._toaster.displaySnackBar(
                        'You are back online.',
                        'success'
                    );
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    retry(): void {
        window.location.reload();
    }
}
